import { RuleId, siteLanguageRule } from '@wix/a11y-rules';
import { Text } from '@wix/design-system';
import { Trans, useErrorHandler, useHttpClient, useTranslation } from '@wix/fe-essentials/react';
import { FixerType, RuleListStatus, RuleTipBox, RuleTipItem } from '@wix/rules-panel-ui';
import { useAtom, useSetAtom } from 'jotai';
import React, { useCallback } from 'react';
import { updateSiteLanguage } from '../../api/update-site-language';
import { currentTaskInfoAtom } from '../../atoms/bi-atoms';
import { ruleApplyTriggeredAtom } from '../../atoms/session-atoms';
import { BiCtaNames } from '../../bi/types';
import { convertScanStatusToUiStatus } from '../../components/fixers/alt-image-fixer/utils';
import { showSaveErrorModal } from '../../components/modals/show-save-error-modal';
import { RuleDescriptionComponent } from '../../components/rule-description-component';
import { SITE_LEVEL_PAGE_ID } from '../../consts';
import { usePanelStateAtom } from '../../hooks/rules-state';
import { useA11yWizardAdapter } from '../../hooks/use-a11y-wizard-adapter';
import { useBiLogger } from '../../hooks/use-bi-logger';
import { useOpenArticleByEditorHost } from '../../hooks/use-open-article-by-editor-host';
import { PanoramaErrorNames, PanoramaTransactions } from '../../panorama/constants';
import { usePanoramaFunctions } from '../../panorama/use-panorama-functions';
import { getFixerDesignPropsFromCapabilities, getFooterButtonTypesFromCapabilities } from '../../utils/get-fixer-design-props';
import { siteLanguageRuleOptions } from '../../utils/site-language-rule-options';
import { useRuleBase } from '../shared/use-rule-base';
import { useUpdateDomOrderRuleData } from './dom-order/use-update-dom-order-rule-data';
import { useUpdateSiteLanguageRuleData } from './use-update-site-language-rule-data';
export const ruleChecker = siteLanguageRule.checker;
export const useSiteLanguageRule = () => {
  const {
    getRuleState
  } = usePanelStateAtom();
  const {
    updateRuleStateAndVerifyIfNeeded,
    setIsApplyTriggered
  } = useRuleBase();
  const setRuleApplyTriggered = useSetAtom(ruleApplyTriggeredAtom);
  const {
    deleteDomOrderRuleData
  } = useUpdateDomOrderRuleData();
  const {
    createSiteLanguageRuleData
  } = useUpdateSiteLanguageRuleData();
  const {
    t
  } = useTranslation();
  const {
    a11yWizardAdapter,
    capabilities,
    editorHost
  } = useA11yWizardAdapter();
  const {
    openSelectedArticle
  } = useOpenArticleByEditorHost();
  const errorHandler = useErrorHandler();
  const biLogger = useBiLogger();
  const {
    finishPanoramaTransaction,
    startPanoramaTransaction,
    reportErrorPanorama
  } = usePanoramaFunctions();
  const httpClient = useHttpClient();
  const [currentTaskInfo, setCurrentTaskInfo] = useAtom(currentTaskInfoAtom);
  const createSiteLanguageRuleResult = useCallback(_ref => {
    var _ruleState$state3;
    let {
      settingsLanguage,
      detectedLanguage,
      multiLingualPrimaryLanguage,
      status,
      isEditableOnceCompleted = true,
      isMultilingualEnabled,
      pageId
    } = _ref;
    const ruleId = RuleId.SITE_LANGUAGE;
    const ruleState = getRuleState(SITE_LEVEL_PAGE_ID, ruleId);
    const optionSettingsLanguage = siteLanguageRuleOptions.find(option => {
      var _ruleState$state, _ruleState$state2;
      return (ruleState == null || (_ruleState$state = ruleState.state) == null ? void 0 : _ruleState$state.settingsLanguage) === undefined ? settingsLanguage === option.id : (ruleState == null || (_ruleState$state2 = ruleState.state) == null ? void 0 : _ruleState$state2.settingsLanguage) === option.id;
    });
    const handleClickApply = async newValue => {
      var _siteLanguageRuleOpti;
      const {
        id
      } = newValue;
      startPanoramaTransaction(PanoramaTransactions.siteLanguageRuleOnApply, {
        id
      });
      biLogger.taskElementCtaClick({
        ctaName: BiCtaNames.Apply,
        index: 0,
        status: uiStatus
      });
      biLogger.taskElementApplyClick({
        index: 0,
        status: uiStatus,
        value: ((_siteLanguageRuleOpti = siteLanguageRuleOptions.find(option => id === option.id)) == null ? void 0 : _siteLanguageRuleOpti.value) ?? ''
      });
      try {
        await updateSiteLanguage(httpClient, errorHandler, id);
        a11yWizardAdapter.reloadSiteProperties();
        try {
          await createSiteLanguageRuleData();
        } catch (error) {
          // Silently handle - saving the language itself succeeded, the manual flag is best-effort
        }
      } catch (e) {
        reportErrorPanorama(e, PanoramaErrorNames.updateSiteLanguage);
        showSaveErrorModal({
          a11yWizardAdapter,
          biLogger,
          editorHost,
          errorMessage: errorHandler.getResolvedError(e).message,
          handleTryAgain: () => handleClickApply(newValue),
          t
        });
        throw e;
      }

      // TODO: This is a dirty hack. Remove setTimeout wrapper once we add siteLanguage to mapStateToProps
      await new Promise(resolve => {
        setTimeout(async () => {
          updateRuleStateAndVerifyIfNeeded(pageId, ruleId, {
            settingsLanguage: id,
            status: convertScanStatusToUiStatus(ruleChecker({
              detectedLanguage,
              isManuallyCompleted: true,
              isMultilingualEnabled,
              settingsLanguage: id
            }).status)
          });
          try {
            await deleteDomOrderRuleData();
          } catch (error) {
            // Silently handle deletion errors to prevent blocking language change (if no value exists - no harm done)
          }
          resolve(null);
        }, 500);
      });
      setIsApplyTriggered(true);
      setRuleApplyTriggered({
        pageId,
        ruleId: RuleId.SITE_LANGUAGE
      });
      finishPanoramaTransaction(PanoramaTransactions.siteLanguageRuleOnApply, {
        id
      });
    };
    const uiStatus = (ruleState == null || (_ruleState$state3 = ruleState.state) == null ? void 0 : _ruleState$state3.status) ?? convertScanStatusToUiStatus(status);
    const value = isMultilingualEnabled ? multiLingualPrimaryLanguage ?? (optionSettingsLanguage == null ? void 0 : optionSettingsLanguage.value) : (optionSettingsLanguage == null ? void 0 : optionSettingsLanguage.value) ?? '';
    const ruleResponse = {
      dataHook: `${pageId}-${ruleId}`,
      description: /*#__PURE__*/React.createElement(RuleDescriptionComponent, {
        i18nKey: uiStatus !== RuleListStatus.failed ? isMultilingualEnabled ? 'accessibility-wizard.rules-list.site-language.description.multilingual' : 'accessibility-wizard.rules-list.site-language.description' : 'accessibility-wizard.rules-list.site-language.description.failed',
        i18nKeyValues: {
          value
        },
        openArticle: () => {
          openSelectedArticle(editorHost, isMultilingualEnabled ? 'editor-package-accessibility-wizard.articles-multilingual-main-language-help' : 'editor-package-accessibility-wizard.articles-main-site-language-help');
          biLogger.taskCtaClick({
            ctaName: BiCtaNames.LearnMore,
            elementCount: 1,
            failedElementCount: Number(uiStatus === RuleListStatus.failed)
          });
        },
        textButtonAriaLabel: t('accessibility-wizard.rules-list.site-language.description.learn-more.aria-label')
      }),
      fixer: FixerType.DropdownRule,
      fixerProps: {
        autoFocus: true,
        completedRuleContent: {
          editButtonLabel: t('accessibility-wizard.common.edit'),
          inputTitle: t('accessibility-wizard.rules-list.site-language.completed-state.input-title'),
          title: t('accessibility-wizard.rules-list.issue-resolved'),
          value
        },
        design: getFixerDesignPropsFromCapabilities(capabilities),
        fieldLabel: t('accessibility-wizard.rules-list.site-language.field-label'),
        footerLabels: {
          buttonTypes: getFooterButtonTypesFromCapabilities(capabilities),
          dismiss: t('accessibility-wizard.rules-list.site-language.footer-button.cancel'),
          submit: t('accessibility-wizard.rules-list.site-language.footer-button.apply')
        },
        initialValue: optionSettingsLanguage,
        onApply: handleClickApply,
        onCancel: () => {
          biLogger.taskElementCtaClick({
            ctaName: BiCtaNames.Cancel,
            index: 0,
            status: uiStatus
          });
        },
        onClick: () => {
          biLogger.taskElementCtaClick({
            ctaName: BiCtaNames.DropdownClick,
            index: 0,
            status: uiStatus
          });
        },
        onEdit: () => {
          biLogger.taskElementCtaClick({
            ctaName: BiCtaNames.Edit,
            index: 0,
            status: uiStatus
          });
        },
        onSelect: selectedValue => {
          biLogger.elementFieldChange({
            index: 0,
            status: uiStatus,
            value: selectedValue
          });
        },
        options: siteLanguageRuleOptions
      },
      id: ruleId,
      isEditableOnceCompleted,
      label: t('accessibility-wizard.rules-list.site-language.title'),
      onRuleToggle: function (toggleType, ruleIndex, initiatedByUser) {
        if (initiatedByUser === void 0) {
          initiatedByUser = false;
        }
        setCurrentTaskInfo(taskInfo => ({
          ...taskInfo,
          status: convertScanStatusToUiStatus(status),
          task_index: ruleIndex,
          task_name: ruleId
        }));
        biLogger.taskExpandCollapse({
          elementCount: 1,
          failedElementCount: Number(uiStatus === RuleListStatus.failed),
          initiatedByUser,
          taskInfo: {
            ...currentTaskInfo,
            status: uiStatus,
            task_index: ruleIndex,
            task_name: ruleId
          },
          toggleType
        });
      },
      onStatusChange: () => {
        biLogger.taskStatusChange({
          elementCount: 1,
          failedElementCount: Number(status === 'failed'),
          status: convertScanStatusToUiStatus(status)
        });
        biLogger.elementStatusChange({
          index: 0,
          status: convertScanStatusToUiStatus(status),
          value: settingsLanguage
        });
      },
      parentGroupId: pageId,
      ruleId,
      status: uiStatus,
      tips: /*#__PURE__*/React.createElement(RuleTipBox, {
        padding: "SP3"
      }, /*#__PURE__*/React.createElement(RuleTipItem, null, /*#__PURE__*/React.createElement(Text, {
        size: "tiny",
        weight: "thin",
        secondary: true
      }, /*#__PURE__*/React.createElement(Trans, {
        components: {
          bold: /*#__PURE__*/React.createElement(Text, {
            size: "tiny",
            weight: "bold",
            secondary: true
          })
        },
        i18nKey: "accessibility-wizard.rules-list.site-language.tips.1"
      }))))
    };
    if (isMultilingualEnabled) {
      return {
        ...ruleResponse,
        fixer: FixerType.EvergreenRule,
        isEditableOnceCompleted: false,
        status: RuleListStatus.passed
      };
    }
    return ruleResponse;
  }, [getRuleState, t, startPanoramaTransaction, biLogger, setIsApplyTriggered, setRuleApplyTriggered, finishPanoramaTransaction, httpClient, errorHandler, a11yWizardAdapter, capabilities, reportErrorPanorama, editorHost, updateRuleStateAndVerifyIfNeeded, openSelectedArticle, setCurrentTaskInfo, currentTaskInfo, deleteDomOrderRuleData, createSiteLanguageRuleData]);
  const getSiteLanguageProps = useCallback(_ref2 => {
    var _find;
    let {
      ruleExecutionResult
    } = _ref2;
    const {
      result,
      context
    } = ruleExecutionResult;
    const {
      settingsLanguage,
      detectedLanguage
    } = context;
    const {
      status
    } = result;
    const {
      isMultilingualEnabled
    } = context;
    const multiLingualPrimaryLanguage = (_find = a11yWizardAdapter.getMultilingualLanguages().find(lang => lang.primary)) == null ? void 0 : _find.name;
    return createSiteLanguageRuleResult({
      detectedLanguage,
      isMultilingualEnabled,
      multiLingualPrimaryLanguage,
      pageId: SITE_LEVEL_PAGE_ID,
      settingsLanguage,
      status
    });
  }, [a11yWizardAdapter, createSiteLanguageRuleResult]);
  return {
    createSiteLanguageRuleResult,
    getSiteLanguageProps
  };
};
//# sourceMappingURL=use-site-language-rule.js.map